'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/utils/shad';
import { VariantProps, cva } from 'class-variance-authority';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

/**
 * Radix treats every focus event as intent to show the tooltip. A closing popover, dropdown or
 * dialog restores focus to its trigger, so a tooltip wrapping that trigger opens with no pointer
 * to leave and no blur to follow, and stays up indefinitely. Restricting focus-opening to
 * `:focus-visible` keeps keyboard users covered while ignoring restored focus.
 * @see https://github.com/radix-ui/primitives/issues/2727
 */
function isKeyboardFocus(target: EventTarget | null) {
  if (!(target instanceof Element)) return true;
  try {
    return target.matches(':focus-visible');
  } catch {
    return true;
  }
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onFocus, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    {...props}
    onFocus={(event) => {
      onFocus?.(event);
      // Radix composes handlers with `checkForDefaultPrevented`, so this skips its own onFocus.
      if (!event.defaultPrevented && !isKeyboardFocus(event.target)) event.preventDefault();
    }}
  />
));
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const tooltipVariants = cva(
  'z-50 max-w-75 overflow-hidden rounded-md border px-4 py-2 text-xs shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
  {
    variants: {
      variant: {
        base: 'text-primary-foreground bg-primary',
        primary: 'text-primary bg-popover',
        wide: 'text-primary bg-popover max-w-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipVariants>
>(({ className, sideOffset = 8, collisionPadding = 12, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(tooltipVariants({ variant: props.variant }), className)}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
